'use client'

import { datadogLogs } from '@datadog/browser-logs'
// NB: We could use @datadog/browser-rum/slim if we didn't need session replay
import { datadogRum } from '@datadog/browser-rum'

import {
    isAbortedAutocompleteRequest,
    isAbortedRscPrefetch,
    isAmplitudeDegradedDelivery,
    isAmplitudeFetchNoise,
    isAmplitudeWorkerFallbackNoise,
    isBrowserExtensionNoise,
    isCrossOriginScriptError,
    isFailedAdTechBeacon,
    isHeadlessBot,
    isOutlookSafeLinksScanner,
    isResizeObserverLoopNoise,
    isThirdPartyAuthNoise,
} from './rum-noise'
import { reportSSRMisuse } from './sentinel'
import type { DatadogClient, DatadogInitConfig } from './types'

export { initializeClientLogger } from './logger/client'

/**
 * SSR sentinel for the DatadogClient interface.
 *
 * Returned from `initializeDatadog` when `typeof window === 'undefined'`
 * so the constructor never calls `datadogLogs.init()` / `datadogRum.init()`
 * during SSR. Every method calls `reportSSRMisuse('DatadogClient.<method>')`
 * — terminal methods return void; `getInternalContext` returns undefined
 * to match its non-fluent return type.
 */
export const makeSentinelDatadogClient = (): DatadogClient => ({
    setTrackingConsent: (_consent) => {
        reportSSRMisuse('DatadogClient.setTrackingConsent')
    },
    setUser: (_user) => {
        reportSSRMisuse('DatadogClient.setUser')
    },
    setUserProperty: (_key, _value) => {
        reportSSRMisuse('DatadogClient.setUserProperty')
    },
    clearUser: () => {
        reportSSRMisuse('DatadogClient.clearUser')
    },
    addError: (_error) => {
        reportSSRMisuse('DatadogClient.addError')
    },
    addAction: (_name, _context) => {
        reportSSRMisuse('DatadogClient.addAction')
    },
    addTiming: (_name, _duration) => {
        reportSSRMisuse('DatadogClient.addTiming')
    },
    addFeatureFlag: (_key, _value) => {
        reportSSRMisuse('DatadogClient.addFeatureFlag')
    },
    setGlobalContext: (_context) => {
        reportSSRMisuse('DatadogClient.setGlobalContext')
    },
    setGlobalContextProperty: (_key, _value) => {
        reportSSRMisuse('DatadogClient.setGlobalContextProperty')
    },
    removeGlobalContextProperty: (_key) => {
        reportSSRMisuse('DatadogClient.removeGlobalContextProperty')
    },
    getInternalContext: () => {
        reportSSRMisuse('DatadogClient.getInternalContext')
        return undefined
    },
    startView: (_name, _context) => {
        reportSSRMisuse('DatadogClient.startView')
    },
})

export const initializeDatadog = (config: DatadogInitConfig): DatadogClient => {
    if (typeof window === 'undefined') {
        return makeSentinelDatadogClient()
    }

    const logsEnabled = config.datadog.logs.enabled
    const rumEnabled = config.datadog.rum.enabled

    if (logsEnabled) {
        datadogLogs.init({
            clientToken: config.datadog.clientToken,
            site: config.datadog.site,
            service: config.service.name,
            env: config.env,
            version: config.service.version,
            sessionSampleRate: config.datadog.logs.sessionSampleRate,
            forwardErrorsToLogs: config.datadog.logs.forwardErrorsToLogs,
            telemetrySampleRate: config.datadog.logs.telemetrySampleRate,
            // Must match the RUM init below — the SDKs share the `_dd_s` cookie.
            useSecureSessionCookie: true,
            silentMultipleInit: true, //NB: Multiple init is just a no-op
            beforeSend: (log) => {
                // First-party analytics beacons (Snowplow + Amplitude) blocked
                // client-side report status 0 (no response) — fire-and-forget and
                // not actionable. Cloudflare bot challenges and ad/tracker blockers
                // are the usual cause. Real server errors (4xx/5xx) still report.
                // Hosts are the per-env collector hosts derived from config (see
                // DatadogInitConfig.analyticsHosts), so dev/pilot/prod each match.
                if (
                    log.http?.status_code === 0 &&
                    config.analyticsHosts.some((host) => log.http?.url?.includes(host))
                ) {
                    return false
                }

                // Third-party ad/measurement beacons that never completed (status 0) —
                // Google Ads conversion, remarketing and GA4 pings fired by the GTM
                // container. Produced by declared crawlers executing our tags and by
                // ad/tracker blockers; neither is actionable from our code. Matched on the
                // parsed hostname (plus the path where the host also serves non-beacon
                // traffic), so first-party failures (e.g. /api/nexus/schedules) and real
                // 4xx/5xx from the same ad hosts still report. See #879.
                if (isFailedAdTechBeacon(log.http?.status_code, log.http?.url)) {
                    return false
                }

                // Aborted Next.js RSC prefetches (status 0 + GET + `_rsc=`) — Next
                // cancels in-flight prefetches on hover/navigation; expected, not a
                // failure. Volume tracks traffic surges (e.g. email blasts). A real RSC
                // failure returns 4xx/5xx and still reports.
                if (isAbortedRscPrefetch(log.http?.method, log.http?.status_code, log.http?.url)) {
                    return false
                }

                // Aborted autocomplete requests (status 0 + GET + autocomplete path) —
                // the search widget fires a debounced GET per keystroke; the browser
                // drops the in-flight request when the user keeps typing, submits, or
                // navigates away. Expected cancellation, not a failure, and the single
                // largest client error-log source. A real backend failure returns 4xx/5xx
                // and still reports (the route also logs it server-side).
                if (
                    isAbortedAutocompleteRequest(
                        log.http?.method,
                        log.http?.status_code,
                        log.http?.url,
                    )
                ) {
                    return false
                }

                // Downgrade cross-origin "Script error." entries to warn — unactionable
                // without a stack trace, but worth keeping for visibility. Matches both
                // the raw and SDK-wrapped forms (see isCrossOriginScriptError).
                if (log.status === 'error' && isCrossOriginScriptError(log.message)) {
                    log.status = 'warn'
                }

                // Downgrade the benign ResizeObserver loop notification to warn — a
                // browser-generated signal with no usable stack, not an app error, but
                // kept faintly visible in case layout thrash ever gets bad.
                if (log.status === 'error' && isResizeObserverLoopNoise(log.message)) {
                    log.status = 'warn'
                }

                // Downgrade expected-under-blocking Amplitude delivery failures (empty
                // remote-config, events dropped after exhausting retries) to warn —
                // routine ad/tracker-blocker noise, but kept as a faint heartbeat so a
                // genuine remote-config or delivery outage still shows up on dashboards.
                if (log.status === 'error' && isAmplitudeDegradedDelivery(log.message)) {
                    log.status = 'warn'
                }

                // Drop the Amplitude SDK's self-healing worker-fallback errors — the SDK
                // falls back to the main thread and continues, so the `error` level is
                // spurious.
                if (log.status === 'error' && isAmplitudeWorkerFallbackNoise(log.message)) {
                    return false
                }

                // Drop browser-extension / in-app-webview chatter that runs in the page
                // but originates in code we don't own (Chrome extension messaging, Phantom
                // wallet injection, iOS webview host bridges). Guarded by a first-party
                // stack-frame check: a matching signature whose stack touches our bundle is
                // kept at error, so a real regression in our code can't be masked.
                if (
                    log.status === 'error' &&
                    isBrowserExtensionNoise(log.message, log.error?.stack)
                ) {
                    return false
                }

                // Drop known Google One Tap / FedCM console noise
                // outright — benign, user-driven third-party auth-SDK errors we can't
                // fix from our code and have no reason to keep. This is a curated
                // allowlist of confirmed-unuseful patterns (see isThirdPartyAuthNoise),
                // so dropping is safe; an unrecognized auth error is deliberately NOT
                // matched and keeps its error status so it still surfaces.
                if (log.status === 'error' && isThirdPartyAuthNoise(log.message)) {
                    return false
                }

                // Drop the Amplitude SDK's own console.error for blocked analytics
                // fetches (paired with the status-0 network logs dropped above) —
                // not actionable. Narrowly matched so genuine SDK errors still surface.
                if (log.status === 'error' && isAmplitudeFetchNoise(log.message)) {
                    return false
                }

                // Outlook Safe Links / email link-preview scanners inject a script that
                // throws `Object Not Found Matching Id:…` — third-party, volume tracks
                // email blasts, never real users. Already dropped from RUM; drop from
                // logs too so it doesn't page on a campaign send.
                if (log.status === 'error' && isOutlookSafeLinksScanner(log.message)) {
                    return false
                }

                return true
            },
        })
    }

    if (rumEnabled) {
        datadogRum.init({
            applicationId: config.datadog.appId,
            clientToken: config.datadog.clientToken,
            site: config.datadog.site,
            service: config.service.name,
            env: config.env,
            version: config.service.version,
            allowedTracingUrls: [
                {
                    // NB: Ignore local-dev-only nextjs resources
                    match: (url: string) =>
                        !url.includes('__nextjs') && !url.includes('/_next/static/webpack/'),
                    propagatorTypes: ['tracecontext'],
                },
            ],
            sessionSampleRate: config.datadog.rum.sessionSampleRate,
            sessionReplaySampleRate: config.datadog.rum.sessionReplaySampleRate,
            traceSampleRate: config.datadog.rum.traceSampleRate,
            traceContextInjection: config.datadog.rum.traceContextInjection,
            trackUserInteractions: config.datadog.rum.trackUserInteractions,
            trackResources: config.datadog.rum.trackResources,
            trackLongTasks: config.datadog.rum.trackLongTasks,
            defaultPrivacyLevel: config.datadog.rum.defaultPrivacyLevel,
            telemetrySampleRate: config.datadog.rum.telemetrySampleRate,
            useSecureSessionCookie: true,
            silentMultipleInit: true, // NB: Multiple init is just a no-op
            beforeSend: (event) => {
                if (isHeadlessBot()) {
                    return false
                }

                if (event.type === 'error') {
                    const message = event.error?.message
                    if (isCrossOriginScriptError(message)) {
                        return false
                    }

                    if (isOutlookSafeLinksScanner(message)) {
                        return false
                    }

                    // Extension / in-app-webview chatter (see logs beforeSend). Guarded by
                    // the same first-party stack-frame check so our own errors are kept.
                    if (isBrowserExtensionNoise(message, event.error?.stack)) {
                        return false
                    }
                }

                return true
            },
        })
    }

    return {
        // User & Consent Management (work with both RUM and Logs)
        setTrackingConsent: (consent) => {
            if (rumEnabled) {
                datadogRum.setTrackingConsent(consent)
            }
            if (logsEnabled) {
                datadogLogs.setTrackingConsent(consent)
            }
        },
        setUser: (user) => {
            // Map visitorId to id (required by Datadog SDK)
            const { visitorId, ...rest } = user
            const ddUser = { id: visitorId, ...rest }
            if (rumEnabled) {
                datadogRum.setUser(ddUser)
            }
            if (logsEnabled) {
                datadogLogs.setUser(ddUser)
            }
        },
        setUserProperty: (key, value) => {
            if (rumEnabled) {
                datadogRum.setUserProperty(key, value)
            }
            if (logsEnabled) {
                datadogLogs.setUserProperty(key, value)
            }
        },
        clearUser: () => {
            if (rumEnabled) {
                datadogRum.clearUser()
            }
            if (logsEnabled) {
                datadogLogs.clearUser()
            }
        },

        // Error Tracking (RUM-only)
        addError: (error) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.addError(error)
        },

        // Custom Metrics & Events (RUM-only)
        addAction: (name, context) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.addAction(name, context as never)
        },
        addTiming: (name, duration) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.addTiming(name, duration)
        },
        addFeatureFlag: (key, value) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.setGlobalContextProperty(`feature_flags.${key}`, value)
        },

        // Global Context Management (RUM-only)
        setGlobalContext: (context) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.setGlobalContext(context as never)
        },
        setGlobalContextProperty: (key, value) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.setGlobalContextProperty(key, value)
        },
        removeGlobalContextProperty: (key) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.removeGlobalContextProperty(key)
        },

        // Session & View Management (RUM-only)
        getInternalContext: () => {
            if (!rumEnabled) {
                return undefined as never
            }
            return datadogRum.getInternalContext() as never
        },
        startView: (name, context) => {
            if (!rumEnabled) {
                return
            }
            datadogRum.startView(name)
            if (context) {
                Object.entries(context).forEach(([key, value]) => {
                    datadogRum.setGlobalContextProperty(`view.${key}`, value)
                })
            }
        },
    }
}
